import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ChevronDownIcon from '@heroicons/react/24/solid/ChevronDownIcon';
import { FunctionComponent, PropsWithChildren } from 'react';
import {
    WidgetDocStep,
    WidgetEventField,
    WidgetRenderMode,
} from '@/models/widget';
import {
    buildEntryScriptTag,
    ResolvedWidgetDoc,
} from '@/pages/widgetEditor/utils';
import {
    DOC_RADIUS,
    DocCode,
    DocText,
    InlineCode,
} from '@/pages/widgetEditor/components/DocCode';

interface WidgetDocSectionProps {
    doc: ResolvedWidgetDoc;
    expanded: boolean;
    onToggle: (expanded: boolean) => void;
}

/**
 * German label for a manifest `renderMode`. `shadow` never appears verbatim
 * in the general documentation's German prose ("HTML-Komponente"), so the
 * chip must be translated rather than showing the raw manifest value.
 */
const RENDER_MODE_LABELS: Record<WidgetRenderMode, string> = {
    shadow: 'HTML-Komponente',
    iframe: 'iframe',
};

/** A titled block of the section; real headings so the page has an outline. */
const DocBlock: FunctionComponent<PropsWithChildren<{ title: string }>> = ({
    title,
    children,
}) => (
    <Box component='section'>
        <Typography variant='h6' component='h4' sx={{ mb: 2 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

/** Bordered, horizontally scrollable frame shared by every table here. */
const DocTable: FunctionComponent<PropsWithChildren<{ head: string[] }>> = ({
    head,
    children,
}) => (
    <TableContainer
        sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: DOC_RADIUS,
            '& tr:last-of-type td': { borderBottom: 0 },
        }}
    >
        <Table size='small'>
            <TableHead>
                <TableRow>
                    {head.map((cell) => (
                        <TableCell key={cell}>{cell}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
        </Table>
    </TableContainer>
);

interface DocStepProps {
    step: WidgetDocStep;
    index: number;
    doc: ResolvedWidgetDoc;
}

/** One numbered step of an integration or adjustment list. */
const DocStep: FunctionComponent<DocStepProps> = ({ step, index, doc }) => (
    <Stack component='li' direction='row' spacing={2}>
        <Box
            aria-hidden
            sx={{
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 600,
            }}
        >
            {index + 1}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant='subtitle2'>{step.title}</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                <DocText>{step.body}</DocText>
            </Typography>
            {step.code && <DocCode>{step.code}</DocCode>}
            {step.parameters && step.parameters.length > 0 && (
                <Stack
                    direction='row'
                    sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}
                >
                    {step.parameters.map((name) => {
                        // Looked up here rather than re-derived: `buildWidgetDoc`
                        // is the only place allowed to know how a step's
                        // camelCase parameter name maps to a manifest attribute.
                        const attribute = doc.attributesByName[name];
                        const label = attribute?.attribute ?? name;
                        const title = attribute
                            ? `${attribute.type}${
                                  attribute.default
                                      ? ` · Standard: ${attribute.default}`
                                      : ''
                              }`
                            : undefined;
                        return (
                            <Chip
                                key={name}
                                size='small'
                                label={label}
                                title={title}
                                sx={{ fontFamily: 'monospace' }}
                            />
                        );
                    })}
                </Stack>
            )}
        </Box>
    </Stack>
);

const DocSteps: FunctionComponent<{
    steps: WidgetDocStep[];
    doc: ResolvedWidgetDoc;
}> = ({ steps, doc }) => (
    <Stack component='ol' spacing={3} sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {steps.map((step, index) => (
            <DocStep
                key={`${step.title}-${index}`}
                step={step}
                index={index}
                doc={doc}
            />
        ))}
    </Stack>
);

/**
 * One event's `detail` payload fields, one level deep. Deliberately does not
 * recurse into `fields`/`items` on nested object/array fields — the manifest
 * type is recursive, but a flat listing already tells an integrator what
 * `event.detail` looks like without an open-ended tree renderer.
 */
const EventDetailFields: FunctionComponent<{
    detail: Record<string, WidgetEventField>;
}> = ({ detail }) => (
    <DocTable head={['Feld', 'Typ', 'Optional', 'Beschreibung']}>
        {Object.entries(detail).map(([name, field]) => (
            <TableRow key={name}>
                <TableCell>
                    <InlineCode>{name}</InlineCode>
                </TableCell>
                <TableCell>{field.type}</TableCell>
                <TableCell>{field.nullable ? 'ja' : 'nein'}</TableCell>
                <TableCell>{field.description ?? '-'}</TableCell>
            </TableRow>
        ))}
    </DocTable>
);

/**
 * One widget's documentation.
 *
 * The authored part is optional — the derived facts below it are rendered
 * either way, so a widget the portal has not documented yet still tells an
 * integrator its tag, its script URL, its attributes and its events.
 */
export const WidgetDocSection: FunctionComponent<WidgetDocSectionProps> = ({
    doc,
    expanded,
    onToggle,
}) => (
    <Accordion
        disableGutters
        // Flat, outlined rows with one radius. `square` switches off MUI's
        // first/last-of-type rounding, which would otherwise win over `sx`.
        square
        elevation={0}
        expanded={expanded}
        onChange={(_event, isExpanded) => onToggle(isExpanded)}
        /*
         * Collapsed sections are removed from the DOM, not just hidden. MUI's
         * default keeps them mounted, which would put eleven attribute tables
         * into the page at once and make "is this section open?" untestable —
         * a collapsed section's text would still be found.
         */
        TransitionProps={{ unmountOnExit: true }}
        sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: DOC_RADIUS,
            overflow: 'hidden',
            '&::before': { display: 'none' },
        }}
    >
        <AccordionSummary
            expandIcon={<ChevronDownIcon style={{ width: 18, height: 18 }} />}
            sx={{ px: 3, '&:hover': { bgcolor: 'action.hover' } }}
        >
            <Stack>
                <Typography variant='subtitle1' component='h3'>
                    {doc.label}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                    {doc.widget.tag}
                </Typography>
            </Stack>
        </AccordionSummary>
        <AccordionDetails
            sx={{ px: 3, pt: 3, pb: 4, borderTop: 1, borderColor: 'divider' }}
        >
            <Stack spacing={5}>
                {doc.documentation ? (
                    <>
                        <Stack spacing={1.5}>
                            <Typography variant='body1'>
                                <DocText>{doc.documentation.summary}</DocText>
                            </Typography>
                            {doc.documentation.usage && (
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    <DocText>{doc.documentation.usage}</DocText>
                                </Typography>
                            )}
                        </Stack>

                        {doc.documentation.requires &&
                            doc.documentation.requires.length > 0 && (
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'action.hover',
                                        borderRadius: DOC_RADIUS,
                                    }}
                                >
                                    <Typography
                                        variant='subtitle2'
                                        sx={{ mb: 1 }}
                                    >
                                        Voraussetzungen
                                    </Typography>
                                    {/*
                                     * A list, not chips: the manifest's
                                     * prerequisites are whole sentences, and a
                                     * 200-character chip is unreadable.
                                     */}
                                    <Box component='ul' sx={{ m: 0, pl: 3 }}>
                                        {doc.documentation.requires.map(
                                            (item, index) => (
                                                <Typography
                                                    component='li'
                                                    variant='body2'
                                                    key={`${item}-${index}`}
                                                >
                                                    <DocText>{item}</DocText>
                                                </Typography>
                                            )
                                        )}
                                    </Box>
                                </Box>
                            )}

                        <DocBlock title='Einbindung'>
                            <DocSteps
                                steps={doc.documentation.integration}
                                doc={doc}
                            />
                        </DocBlock>

                        {doc.documentation.adjustment &&
                            doc.documentation.adjustment.length > 0 && (
                                <DocBlock title='Anpassung'>
                                    <DocSteps
                                        steps={doc.documentation.adjustment}
                                        doc={doc}
                                    />
                                </DocBlock>
                            )}
                    </>
                ) : (
                    <Typography variant='body2' color='text.secondary'>
                        Für dieses Widget liegt noch keine ausformulierte
                        Dokumentation vor. Die folgenden Angaben stammen direkt
                        aus dem Widget-Verzeichnis.
                    </Typography>
                )}

                <DocBlock title='Technische Referenz'>
                    <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {doc.renderMode && (
                            <Chip
                                size='small'
                                label={RENDER_MODE_LABELS[doc.renderMode]}
                            />
                        )}
                        <Chip
                            size='small'
                            variant='outlined'
                            label={doc.widget.tag}
                            sx={{ fontFamily: 'monospace' }}
                        />
                    </Stack>
                    {doc.scriptUrl && (
                        <DocCode>{buildEntryScriptTag(doc.scriptUrl)}</DocCode>
                    )}

                    <Typography variant='subtitle2' sx={{ mt: 4, mb: 1.5 }}>
                        Attribute
                    </Typography>
                    {doc.attributes.length > 0 ? (
                        <DocTable
                            head={[
                                'Attribut',
                                'Typ',
                                'Standard',
                                'Anpassbar',
                                'Beschreibung',
                            ]}
                        >
                            {doc.attributes.map((attribute) => (
                                <TableRow key={attribute.name}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <InlineCode>
                                            {attribute.attribute}
                                        </InlineCode>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {attribute.options?.length
                                            ? attribute.options.join(' | ')
                                            : attribute.type}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {attribute.default ?? '-'}
                                    </TableCell>
                                    <TableCell>
                                        {attribute.adjustable ? 'Design' : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {attribute.description ? (
                                            <DocText>
                                                {attribute.description}
                                            </DocText>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </DocTable>
                    ) : (
                        <Typography variant='body2' color='text.secondary'>
                            Dieses Widget hat keine Attribute.
                        </Typography>
                    )}

                    {doc.events.length > 0 && (
                        <>
                            <Typography
                                variant='subtitle2'
                                sx={{ mt: 4, mb: 1.5 }}
                            >
                                Events
                            </Typography>
                            {/* One block per event, its payload right under it. */}
                            <Stack spacing={3}>
                                {doc.events.map(({ name, event }) => (
                                    <Box key={name}>
                                        <Stack
                                            direction='row'
                                            spacing={1}
                                            alignItems='center'
                                            sx={{ flexWrap: 'wrap' }}
                                        >
                                            <InlineCode>{name}</InlineCode>
                                            {event.handlerProp && (
                                                <Typography
                                                    variant='caption'
                                                    color='text.secondary'
                                                >
                                                    {`Handler: ${event.handlerProp}`}
                                                </Typography>
                                            )}
                                        </Stack>
                                        {event.description && (
                                            <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{ mt: 0.5 }}
                                            >
                                                <DocText>
                                                    {event.description}
                                                </DocText>
                                            </Typography>
                                        )}
                                        {event.detail &&
                                            Object.keys(event.detail).length >
                                                0 && (
                                                <Box sx={{ mt: 1.5 }}>
                                                    <EventDetailFields
                                                        detail={event.detail}
                                                    />
                                                </Box>
                                            )}
                                    </Box>
                                ))}
                            </Stack>
                        </>
                    )}
                </DocBlock>
            </Stack>
        </AccordionDetails>
    </Accordion>
);
